import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

/**
 * Shared empty / zero-state. An optional icon in a soft brand-tinted circle,
 * a display-font title, an optional muted description, and an optional
 * action. Every colour comes from theme tokens so it re-skins with the
 * palette.
 *
 * Use it for empty queues, empty lists, and "nothing here yet" panels in
 * place of ad-hoc one-off markup.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <Box
      data-slot="empty-state"
      className={className}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        px: 3,
        py: 6,
        textAlign: "center",
      }}
    >
      {icon && (
        <Box
          aria-hidden
          sx={{
            display: "flex",
            height: 48,
            width: 48,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
            color: "primary.main",
            "& svg": { height: 24, width: 24 },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ display: "grid", gap: 0.5 }}>
        <Typography
          sx={{
            fontFamily: '"DM Sans", "Inter", ui-sans-serif, system-ui, sans-serif',
            fontSize: 14,
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            data-testid="empty-state-description"
            sx={{ mx: "auto", maxWidth: "24rem", fontSize: 12, lineHeight: 1.4, color: "text.secondary" }}
          >
            {description}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
    </Box>
  );
}
